import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";

import {
  useGetAdminV2MeQuery,
  useGetDemoOpsAccessAssignmentsQuery,
  useGetUsageAnalyticsQuery,
} from "@/store/api/baseApi";
import { STAGING_APP_ORIGIN } from "@/utils/canonicalUrls";
import {
  getErrorMessage,
  getPublicErrorDetails,
  toError,
} from "@/utils/errorHandling";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";
import {
  VNextButton,
  VNextCard,
  VNextKeyValueList,
  VNextNotice,
  VNextStat,
} from "@/vnext/primitives";
import { DemoOpsAssignmentsCard } from "@/vnext/routes/settings/demo-ops/DemoOpsAssignmentsCard";
import {
  buildAnonUsageSummary,
  buildDefaultDemoOpsFilters,
  buildDemoAccessRows,
  buildUserUsageSummaries,
  filterDemoAccessRows,
  hasDemoOpsSensitiveAccess,
  sortDemoAccessRows,
} from "@/vnext/routes/settings/demo-ops/demoOpsRouteModel";
import { SettingsWorkspaceChrome } from "@/vnext/routes/settings/SettingsWorkspaceChrome";
import { formatVNextDate, formatVNextNumber } from "@/vnext/shared/formatters";

import "@/vnext/routes/settings/demo-ops/demoOpsMake.css";

const logger = FrontendLoggerFactory.getLogger().withComponent("DemoOpsRoute");
const PREVIEW_SITE_BASE_URL = STAGING_APP_ORIGIN;

export function DemoOpsRoute() {
  const pageStartedAtRef = useRef<number | null>(null);
  const readyLoggedRef = useRef(false);
  const [filters] = useState(() => buildDefaultDemoOpsFilters());
  const [showInactiveAssignments, setShowInactiveAssignments] = useState(false);
  const { data: meData } = useGetAdminV2MeQuery();
  const {
    data: assignments,
    error: assignmentsError,
    isLoading: assignmentsLoading,
  } = useGetDemoOpsAccessAssignmentsQuery();
  const usageFilters = useMemo(
    () => ({ ...filters, view: "demo-ops" as const }),
    [filters],
  );
  const {
    data: usage,
    error: usageError,
    isFetching: usageFetching,
    isLoading: usageLoading,
    refetch: refetchUsage,
  } = useGetUsageAnalyticsQuery(usageFilters);

  const usageByUserId = useMemo(
    () => buildUserUsageSummaries(usage?.customerUsage ?? []),
    [usage],
  );
  const anonUsageSummary = useMemo(
    () => buildAnonUsageSummary(usageByUserId),
    [usageByUserId],
  );
  const canViewSensitiveIdentifiers = useMemo(
    () => hasDemoOpsSensitiveAccess(meData?.permissionKeys ?? []),
    [meData?.permissionKeys],
  );
  const demoAccessRows = useMemo(
    () =>
      sortDemoAccessRows(
        buildDemoAccessRows(assignments, usageByUserId, {
          includeSensitiveIdentifiers: canViewSensitiveIdentifiers,
        }),
      ),
    [assignments, canViewSensitiveIdentifiers, usageByUserId],
  );
  const visibleDemoAccessRows = useMemo(
    () =>
      filterDemoAccessRows(demoAccessRows, {
        includeInactiveAssignments: showInactiveAssignments,
      }),
    [demoAccessRows, showInactiveAssignments],
  );
  const isLoading = assignmentsLoading || usageLoading;
  const hasError = Boolean(assignmentsError) || Boolean(usageError);

  useEffect(() => {
    logger.debug("DEMO_OPS_ROUTE_ENTER", {
      component: "DemoOpsRoute",
      feature: "ADMIN",
      operation: "DEMO_OPS_ROUTE_ENTER",
    });
    pageStartedAtRef.current = Date.now();
    readyLoggedRef.current = false;
  }, []);

  useEffect(() => {
    if (readyLoggedRef.current || isLoading || hasError) {
      return;
    }

    readyLoggedRef.current = true;
    logger.debug("DEMO_OPS_ROUTE_READY", {
      activeAssignments: assignments?.filter(
        (assignment) => assignment.demoAccessEnabled,
      ).length,
      component: "DemoOpsRoute",
      feature: "ADMIN",
      operation: "DEMO_OPS_ROUTE_READY",
      pageLoadDurationMs:
        pageStartedAtRef.current === null
          ? null
          : Date.now() - pageStartedAtRef.current,
      totalAssignments: assignments?.length ?? 0,
      totalUsageMinutes: usage?.totals.totalActiveMinutes ?? 0,
    });
  }, [assignments, hasError, isLoading, usage?.totals.totalActiveMinutes]);

  useEffect(() => {
    if (!assignmentsError && !usageError) {
      return;
    }

    const primaryError = assignmentsError ?? usageError;
    const details = getPublicErrorDetails(
      primaryError,
      "Failed to load demo ops diagnostics.",
    );

    logger.error(
      "DEMO_OPS_ROUTE_FAIL",
      toError(primaryError) ?? new Error("demo_ops_route_failed"),
      {
        component: "DemoOpsRoute",
        correlationId: details.correlationId ?? undefined,
        errorCode: details.errorCode,
        feature: "ADMIN",
        operation: "DEMO_OPS_ROUTE_FAIL",
      },
    );
  }, [assignmentsError, usageError]);

  const assignmentErrorMessage = assignmentsError
    ? getErrorMessage(assignmentsError, "Failed to load demo access data.")
    : null;
  const usageErrorMessage = usageError
    ? getErrorMessage(usageError, "Failed to load demo usage data.")
    : null;
  const activeAssignments =
    assignments?.filter((assignment) => assignment.demoAccessEnabled).length ??
    0;

  return (
    <SettingsWorkspaceChrome
      actions={
        <div className="demo-ops-route__toolbar">
          <Link
            className="demo-ops-route__toolbar-link vnext-button vnext-button--primary"
            to="/admin/demo-access"
          >
            Manage demo access
          </Link>
          <a
            className="demo-ops-route__toolbar-link vnext-button vnext-button--secondary"
            href={`${PREVIEW_SITE_BASE_URL}/login`}
            rel="noreferrer"
            target="_blank"
          >
            Open preview login
          </a>
          <VNextButton
            disabled={usageFetching}
            onClick={() => void refetchUsage()}
            tone="secondary"
          >
            {usageFetching ? "Refreshing…" : "Refresh usage"}
          </VNextButton>
        </div>
      }
      subtitle="Check demo access coverage and preview-only usage from one place."
      title="Demo Ops"
    >
      <div className="demo-ops-route">
        <VNextNotice tone="info">
          Use this page to check who still has demo access, how much preview
          activity happened, and whether the demo data needs a reset.
        </VNextNotice>

        {hasError ? (
          <VNextNotice tone="danger">
            {assignmentErrorMessage ?? usageErrorMessage ?? ""}
          </VNextNotice>
        ) : null}

        {!canViewSensitiveIdentifiers ? (
          <VNextNotice tone="warning">
            User names, emails, and Firebase UIDs stay hidden until your account
            has sensitive analytics access.
          </VNextNotice>
        ) : null}

        <div className="demo-ops-route__stats">
          <VNextStat
            detail="Within the selected demo analytics window."
            label="Total active minutes"
            value={formatVNextNumber(
              Math.round(usage?.totals.totalActiveMinutes ?? 0),
            )}
          />
          <VNextStat
            detail="Distinct demo-access operators seen in usage."
            label="Active users"
            value={formatVNextNumber(usage?.totals.activeUsers ?? 0)}
          />
          <VNextStat
            detail="Assignments currently enabled for preview use."
            label="Active assignments"
            value={formatVNextNumber(activeAssignments)}
          />
          <VNextStat
            detail={`${formatVNextNumber(
              Math.round(anonUsageSummary.totalActiveMinutes),
            )} min anonymous activity`}
            label="Anonymous sessions"
            value={formatVNextNumber(anonUsageSummary.sessionCount)}
          />
        </div>

        <div className="demo-ops-route__meta-grid">
          <VNextCard
            subtitle="The demo analytics query is pinned to the dedicated demo organisation."
            title="Usage window"
          >
            <VNextKeyValueList
              items={[
                {
                  key: "window_start",
                  label: "Window start",
                  value: formatVNextDate(filters.startDate),
                },
                {
                  key: "window_end",
                  label: "Window end",
                  value: formatVNextDate(filters.endDate),
                },
                {
                  key: "timeframe_response",
                  label: "API timeframe",
                  value: usage
                    ? `${formatVNextDate(
                        usage.timeframe.startDate,
                      )} to ${formatVNextDate(usage.timeframe.endDate)}`
                    : "Waiting for usage data",
                },
                {
                  key: "active_orgs",
                  label: "Active organisations",
                  value: formatVNextNumber(
                    usage?.totals.activeOrganisations ?? 0,
                  ),
                },
              ]}
            />
          </VNextCard>
          <VNextCard
            subtitle="Use this when demo data looks stale or operators report missing preview fixtures."
            title="Seed freshness"
          >
            <VNextKeyValueList
              items={[
                {
                  key: "assignments",
                  label: "Tracked assignments",
                  value: formatVNextNumber(assignments?.length ?? 0),
                },
                {
                  key: "reset_path",
                  label: "If data looks stale",
                  value: "Use Reset demo data from the preview demo banner.",
                },
                {
                  key: "seed_version",
                  label: "Fixture updates",
                  value:
                    "Bump the demo seed version whenever the preview fixtures change.",
                },
              ]}
            />
          </VNextCard>
        </div>

        <DemoOpsAssignmentsCard
          canViewSensitiveIdentifiers={canViewSensitiveIdentifiers}
          inactiveAssignmentsCount={demoAccessRows.length - activeAssignments}
          isLoading={isLoading}
          onToggleShowInactiveAssignments={() => {
            setShowInactiveAssignments((previous) => !previous);
          }}
          rows={visibleDemoAccessRows}
          showInactiveAssignments={showInactiveAssignments}
        />
      </div>
    </SettingsWorkspaceChrome>
  );
}

export default DemoOpsRoute;
